import { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { hydrate, QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { I18nProvider } from '@cx-modules/i18n-provider';
import { DefaultLoadingFallback } from '@cx-modules/react-utils';
import type { Country, DeviceType, Repub, StateCode } from '@cx-modules/types';

import MoreArticlesWrapper from './MoreArticles';
import type { MoreArticlesIndexProps } from './types';

const createQueryClient = (nocache?: boolean) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: nocache ? 0 : undefined,
        refetchOnMount: nocache ? true : false,
        retry: (failureCount, error: any) => {
          // Don't retry on 404s as they indicate a valid "no content" response
          if (error?.response?.status === 404) {
            return false;
          }
          // For other errors, retry up to 2 times
          return failureCount < 2;
        },
        retryDelay: attemptIndex => Math.min(1000 * (attemptIndex + 1), 3000),
        staleTime: nocache ? 0 : Infinity,
      },
    },
  });

const ResetOnMount: React.FC<{ resetErrorBoundary: () => void }> = ({ resetErrorBoundary }) => {
  useEffect(() => {
    resetErrorBoundary();
  }, [resetErrorBoundary]);
  return null;
};

function MoreArticlesWithQuery({ dehydratedState, nocache, ...props }: MoreArticlesIndexProps) {
  const [queryClient] = useState(() => {
    const client = createQueryClient(nocache);
    hydrate(client, dehydratedState);
    return client;
  });

  return (
    <ReakitProvider>
      <QueryClientProvider client={queryClient}>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              fallbackRender={({ resetErrorBoundary }) => <ResetOnMount resetErrorBoundary={resetErrorBoundary} />}
              onReset={reset}
            >
              <I18nProvider i18nInstance={props.i18n}>
                <Suspense fallback={<DefaultLoadingFallback />}>
                  <MoreArticlesWrapper {...props} />
                </Suspense>
              </I18nProvider>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </QueryClientProvider>
    </ReakitProvider>
  );
}

// The props type is part of the public API — every consumer wraps this component and types its
// own props with it. Re-exported explicitly: the dist `.d.ts` dts-bundle-generator used to emit
// hoisted it automatically, and since NGF-6661 this file IS the package's type surface.
export { ArticleTypes, type MoreArticlesIndexProps, type PaginationMetadata } from './types';
export { loadDataForServer } from './loadData';
export { MoreArticlesWithQuery };
// The published @cx-modules/more-articles exposed these @cx-modules/types types so consuming apps can
// import them from this module (atlas-astro's page-template reads Country/DeviceType/StateCode).
export type { Country, DeviceType, Repub, StateCode };
